import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigation, useRoute } from '@react-navigation/native'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Platform } from 'react-native'
import styled from 'styled-components/native'

import { getProfileHookConfig } from 'features/navigation/navigators/ProfileStackNavigator/getProfileHookConfig'
import { UseNavigationType, UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { useChangeEmailSetPasswordMutation } from 'features/profile/queries/useChangeEmailSetPasswordMutation'
import { eventMonitoring } from 'libs/monitoring/services'
import { PasswordInputController } from 'shared/forms/controllers/PasswordInputController'
import { newPasswordSchema } from 'shared/forms/schemas/newPasswordSchema'
import { Form } from 'ui/components/Form'
import { useForHeightKeyboardEvents } from 'ui/components/keyboard/useKeyboardEvents'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { Button } from 'ui/designSystem/Button/Button'
import { showErrorSnackBar, showSuccessSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { PageWithHeader } from 'ui/pages/PageWithHeader'
import { Typo } from 'ui/theme'
import { setTextSemantic } from 'ui/theme/typographyAttrs/setTextSemantic'

type FormValues = {
  newPassword: string
  confirmedPassword: string
}

export const ChangeEmailSetPassword = () => {
  const { replace } = useNavigation<UseNavigationType>()
  const { params } = useRoute<UseRouteType<'ChangeEmailSetPassword'>>()
  const [keyboardHeight, setKeyboardHeight] = useState(0)
  useForHeightKeyboardEvents(setKeyboardHeight)

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      newPassword: '',
      confirmedPassword: '',
    },
    resolver: yupResolver(newPasswordSchema),
    mode: 'onChange',
  })

  const { mutate: setPassword, isPending } = useChangeEmailSetPasswordMutation({
    onSuccess: () => {
      showSuccessSnackBar('Ton mot de passe a bien été créé.')
      if (!params?.emailSelectionToken) return // emailSelectionToken should never be undefined if token is defined
      replace(...getProfileHookConfig('NewEmailSelection', { token: params?.emailSelectionToken }))
    },
    onError: () =>
      showErrorSnackBar(
        'Une erreur s’est produite lors de la création du mot de passe. Réessaie plus tard.'
      ),
  })

  const onSubmit = handleSubmit(({ newPassword }) => {
    if (!params?.token || typeof params?.token !== 'string') {
      eventMonitoring.captureException(
        new Error(`Expected a string, but received ${typeof params?.token}`)
      )
      return
    }
    setPassword({ resetPasswordToken: params?.token, newPassword })
  })

  return (
    <PageWithHeader
      shouldLimitWidth
      title="Créer mon mot de passe"
      scrollChildren={
        <StyledView paddingBottom={Platform.OS === 'ios' ? keyboardHeight : 0}>
          <ViewGap gap={4}>
            <Typo.Title3 {...setTextSemantic('h2')}>Crée ton mot de passe</Typo.Title3>
            <Typo.Body>
              Tu t’es inscrit via Google ou Apple, tu ne possèdes donc pas de mot de passe
              actuellement.
            </Typo.Body>
            <Typo.Body>
              Ce mot de passe te permettra de te connecter avec ta nouvelle adresse e-mail.
            </Typo.Body>
          </ViewGap>
          <Form.MaxWidth flex={1}>
            <Container>
              <PasswordInputController
                name="newPassword"
                label="Mot de passe"
                autocomplete="new-password"
                control={control}
                requiredIndicator="explicit"
                displayValidation
              />
            </Container>
            <Container>
              <PasswordInputController
                name="confirmedPassword"
                autocomplete="new-password"
                label="Confirmer le mot de passe"
                control={control}
                requiredIndicator="explicit"
              />
            </Container>
            <Container>
              <Button
                wording="Créer mon mot de passe"
                disabled={!isValid}
                isLoading={isPending}
                onPress={onSubmit}
              />
            </Container>
          </Form.MaxWidth>
        </StyledView>
      }
    />
  )
}

const StyledView = styled.View<{ paddingBottom: number }>(({ paddingBottom }) => ({
  paddingBottom,
}))

const Container = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xxxl,
}))
